/**
 * RespetarHorarioCard — flag por empresa "Respetar el horario de tus equipos".
 * Extraído de ProductsPage (R4) y reubicado a TeamConfigPage (tab Guardias), donde
 * lo ven los admins de TODAS las verticales (ProductsPage está oculta para
 * car_dealer y developer — CLAUDE.md §3 #7b).
 *
 * Endpoints (ya existentes desde R1):
 *  GET /api/roulette/config → respetar_horario_grupos  (ON por default: !== false)
 *  PUT /api/roulette/config → { respetar_horario_grupos: bool }
 *
 * El OFF (ON→OFF) abre un modal de advertencia comercial: solo persiste si el
 * usuario confirma; cancelar/cerrar deja el toggle en ON y NO guarda. OFF→ON
 * persiste directo, sin modal.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

export default function RespetarHorarioCard() {
  const [respetarHorario, setRespetarHorario] = useState(true); // ON por default
  const [offWarnOpen, setOffWarnOpen] = useState(false);

  const load = async () => {
    try {
      const rRes = await api.get('/roulette/config').catch(() => ({ data: {} }));
      setRespetarHorario(rRes.data?.respetar_horario_grupos !== false); // ON salvo que sea explícitamente false
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const changeRespetarHorario = async (value) => {
    setRespetarHorario(value);
    try {
      await api.put('/roulette/config', { respetar_horario_grupos: value });
      toast.success('Configuración actualizada');
    } catch (e) {
      setRespetarHorario(!value); // revertir en fallo
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  return (
    <>
      <Card className="bg-card/50 border-white/5" data-testid="respetar-horario-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" /> Respetar el horario de tus equipos
          </CardTitle>
          <CardDescription className="text-muted-foreground text-xs">
            {respetarHorario
              ? 'Los prospectos que llegan fuera del horario de un equipo esperan atendidos por el asistente y se asignan en cuanto el equipo abre. Nadie recibe avisos a deshoras.'
              : 'Los prospectos se asignan de inmediato a un vendedor del equipo aunque sea fuera de su horario. Podrían recibir avisos a cualquier hora.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
            <div>
              <p className="text-sm text-white">Respetar el horario de los equipos</p>
              <p className="text-[10px] text-muted-foreground">Aplica a toda la empresa</p>
            </div>
            <Switch
              checked={respetarHorario}
              onCheckedChange={(val) => { if (val) { changeRespetarHorario(true); } else { setOffWarnOpen(true); } }}
              data-testid="respetar-horario-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Advertencia al APAGAR: solo persiste si el usuario confirma; cancelar deja el toggle en ON */}
      <Dialog open={offWarnOpen} onOpenChange={setOffWarnOpen}>
        <DialogContent className="max-w-md" data-testid="respetar-horario-warning">
          <DialogHeader>
            <DialogTitle className="text-sm">Respetar el horario de tus equipos</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Si apagas esto, tus vendedores podrán recibir prospectos y notificaciones a cualquier hora, incluso de madrugada. ¿Seguro que quieres asignar sin respetar su horario?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" className="text-[11px]" onClick={() => setOffWarnOpen(false)} data-testid="respetar-horario-cancel">
              Mantener el horario
            </Button>
            <Button variant="destructive" size="sm" className="text-[11px]" onClick={() => { changeRespetarHorario(false); setOffWarnOpen(false); }} data-testid="respetar-horario-confirm">
              Sí, asignar sin horario
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
